import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useNavigate } from 'react-router-dom';

const DailyPromptModal = ({ isOpen, onClose }) => {
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const navigate = useNavigate();

  const journalPrompts = [
    {
      category: "Gratitude & Reflection",
      prompt: "What are three small moments from today that brought you joy? How did they make you feel?",
      icon: "🌟"
    },
    {
      category: "Nature Connection",
      prompt: "Describe a natural element you encountered today (sky, trees, flowers, etc.). How did it affect your mood?",
      icon: "🌿"
    },
    {
      category: "Emotional Awareness",
      prompt: "What emotion visited you most today? Write a letter to that emotion, acknowledging its presence.",
      icon: "💭"
    },
    {
      category: "Growth & Learning",
      prompt: "What is one thing you learned about yourself today? How might this insight help you tomorrow?",
      icon: "🌱"
    },
    {
      category: "Mindful Moments",
      prompt: "Describe a moment today when you felt fully present. What were you doing, and what did you notice?",
      icon: "🧘‍♀️"
    },
    {
      category: "Challenges & Strength",
      prompt: "What challenge did you face today, and what strength did you discover within yourself to handle it?",
      icon: "💪"
    },
    {
      category: "Connection & Love",
      prompt: "Who made you feel supported or loved today? How can you express gratitude for their presence?",
      icon: "💖"
    },
    {
      category: "Dreams & Hopes",
      prompt: "If today was a stepping stone toward your dreams, what step forward did you take, no matter how small?",
      icon: "✨"
    },
    {
      category: "Self-Compassion",
      prompt: "What would you say to comfort a dear friend who had your exact day? Write those same words to yourself.",
      icon: "🤗"
    },
    {
      category: "Creativity & Expression",
      prompt: "How did you express your creativity today? If you didn't, what's one creative thing you'd like to try tomorrow?",
      icon: "🎨"
    },
    {
      category: "Peace & Calm",
      prompt: "What brought you a sense of peace today? How can you invite more of this feeling into your life?",
      icon: "🕊️"
    },
    {
      category: "Adventure & Curiosity",
      prompt: "What sparked your curiosity today? What would you like to explore or learn more about?",
      icon: "🔍"
    },
    {
      category: "Body & Wellness",
      prompt: "How did your body feel today? What is it asking for - rest, movement, nourishment, or care?",
      icon: "🌸"
    },
    {
      category: "Seasons & Change",
      prompt: "What season of change are you in right now? What are you letting go of, and what are you welcoming?",
      icon: "🍃"
    },
    {
      category: "Inner Voice",
      prompt: "If your wisest, most compassionate self could give you one piece of advice right now, what would it be?",
      icon: "💫"
    },
    {
      category: "Simple Pleasures",
      prompt: "What simple pleasure did you enjoy today? How can you savor more of these moments?",
      icon: "☕"
    },
    {
      category: "Weather & Mood",
      prompt: "How did today's weather match or contrast with your inner weather? What would perfect weather feel like inside you?",
      icon: "🌤️"
    },
    {
      category: "Evening Reflection",
      prompt: "As the day comes to a close, what are you most proud of? What are you ready to release?",
      icon: "🌙"
    },
    {
      category: "Future Self",
      prompt: "What would your future self thank your present self for doing or feeling today?",
      icon: "⏰"
    },
    {
      category: "Breath & Being",
      prompt: "Take three deep breaths. What do you notice in your body, mind, and heart right now?",
      icon: "🌊"
    }
  ];

  useEffect(() => {
    if (isOpen && !currentPrompt) {
      // Get today's date as seed for consistent daily prompt
      const today = new Date().toDateString();
      const promptIndex = hashCode(today) % journalPrompts.length;
      setCurrentPrompt(journalPrompts[promptIndex]);
    }
  }, [isOpen]);

  // Simple hash function to get consistent daily prompt
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  const handleJournalNow = () => {
    localStorage.setItem('promptModalShown', new Date().toDateString());
    navigate('/journal');
    onClose();
  };

  const handleMaybeLater = () => {
    localStorage.setItem('promptModalShown', new Date().toDateString());
    onClose();
  };

  if (!currentPrompt) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="daily-prompt-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span className="text-2xl">{currentPrompt.icon}</span>
            <span>Your Daily Reflection</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-blue-600 font-medium">
            {currentPrompt.category}
          </DialogDescription>
        </DialogHeader>
        
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-amber-50 border-blue-200">
          <p className="text-gray-800 leading-relaxed text-center italic">
            "{currentPrompt.prompt}"
          </p>
        </Card>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            onClick={handleJournalNow}
            className="btn-primary flex-1"
            data-testid="journal-now-btn"
          >
            📝 Journal Now
          </Button>
          <Button 
            onClick={handleMaybeLater}
            variant="outline"
            className="btn-secondary flex-1"
            data-testid="maybe-later-btn"
          >
            Maybe Later
          </Button>
        </div>
        
        <div className="text-center">
          <p className="text-xs text-gray-500">
            💡 New prompts appear daily to inspire your wellness journey
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DailyPromptModal;
